import { Button } from "@mui/joy";
import { useEffect, useRef, useState } from "react";
import {
  loadTossPayments,
  TossPaymentsPayment,
} from "@tosspayments/tosspayments-sdk";
import {
  getPromotionBySlug,
  getPromotionProgramDays,
  PlanKey,
  planKeyToName,
} from "../planTypes";
import { PaymentOrderType } from "api/paymentApi";
import { consumePartnerAutopay } from "utils/partnerAuth";
import { useParams } from "react-router-dom";
import { useRecoilValue } from "recoil";
import { userAtom } from "recoil/userAtom";
import useMixpanelWeb from "hooks/useMixpanelWeb";

// 파트너(메인 인증) 결제 — 현재 페이지에서 토스 결제창을 인라인으로 띄운다.
// 메인 주문(paymentOrderState)의 orderId/customerKey 사용, 성공 시 메인 /toss-payments/success 로 confirm.
// amount 는 표시 가격(selectedPlanObj.price)과 동일하게 부모가 전달.
export default function PartnerTossButton({
  order,
  selectedPlan,
  amount,
}: {
  order: PaymentOrderType;
  selectedPlan: PlanKey;
  amount: number;
}) {
  const clientKey = process.env.REACT_APP_TOSSPAYMENTS_CLIENT_KEY!!;
  const { slug } = useParams();
  const [tossPayment, setTossPayment] = useState<TossPaymentsPayment | null>();
  const autoPaidRef = useRef(false);
  const user = useRecoilValue(userAtom);
  const isSubscribing = user?.subscription?.isSubscribing === true;
  const [loading, setLoading] = useState(false);

  // 파트너 단독 페이지는 URL slug 가 단일 소스 (PaymentContentPartner 와 동일 규칙)
  const promotion = getPromotionBySlug(slug);
  const programDays = getPromotionProgramDays(promotion);

  const setMixPanelPayClick = useMixpanelWeb({
    eventName: "제휴 결제 버튼 클릭",
    params: {
      event_source: "web",
    },
  });

  useEffect(() => {
    let active = true;
    async function load() {
      if (clientKey && order?.customerKey) {
        const tossPayments = await loadTossPayments(clientKey);
        const payment = tossPayments.payment({
          customerKey: order.customerKey,
        });
        if (active) setTossPayment(payment);
      }
    }
    load();
    return () => {
      active = false;
    };
  }, [clientKey, order?.customerKey]);

  const requestPayment = async () => {
    if (!tossPayment || !order?.orderId) return;
    setLoading(true);
    try {
      await tossPayment.requestPayment({
        method: "CARD",
        amount: { currency: "KRW", value: amount },
        orderId: order.orderId,
        orderName: `디스턴싱 ${planKeyToName[selectedPlan]} 플랜 / ${programDays}일`,
        successUrl:
          window.location.origin + `/partner/${slug}/toss-payments/success`,
        failUrl: window.location.origin + `/partner/${slug}/toss-payments/fail`,
        customerEmail: order.customerEmail,
        customerName: order.customerName,
        card: {
          useEscrow: false,
          flowMode: "DEFAULT",
          useCardPoint: false,
          useAppCardOnly: false,
        },
      });
    } catch (error) {
      setLoading(false);
      console.error(error);
    }
  };

  // 로그인 복귀 후 1회 자동 오픈 (PartnerLogin 이 세팅한 autopay 플래그 소비)
  useEffect(() => {
    if (autoPaidRef.current || !tossPayment) return;
    if (!consumePartnerAutopay()) return;
    autoPaidRef.current = true;
    requestPayment();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tossPayment]);

  return (
    <Button
      fullWidth
      size="lg"
      color="primary"
      loading={loading}
      disabled={!tossPayment || isSubscribing}
      onClick={() => {
        // 로그인 복귀 autopay 자동 오픈 경로에선 미발사 — 로그아웃 버튼 클릭에서 이미 집계 (의도당 1회)
        setMixPanelPayClick({
          promotion: promotion ?? "",
        });
        requestPayment();
      }}
      sx={{ width: "100%", fontWeight: 700 }}
    >
      결제하기
    </Button>
  );
}
